


import { create } from 'zustand';
import brain from 'brain';

export interface StaffUser {
  uid: string;
  name: string;
  email?: string;
  photoURL?: string;
  role?: string;
  isActive?: boolean;
  employmentType?: string;  // Add employmentType field
  isSuperAdmin?: boolean;  // Super admin status
  hideFromClientView?: boolean;  // Hidden from client view
  lastUpdated: number;
}

interface StaffStore {
  // State
  staffUsers: Map<string, StaffUser>;
  isLoading: boolean;
  lastFetchTime: number;
  
  // Actions
  getStaffUser: (uid: string) => StaffUser | null;
  fetchStaffUser: (uid: string) => Promise<StaffUser>;
  fetchAllStaff: () => Promise<void>;
  loadAllStaffMembers: () => Promise<StaffUser[]>; // New method for bulk loading
  updateStaffUser: (uid: string, updates: Partial<StaffUser>) => void;
  clearStaff: () => void;
  forceRefreshStaff: () => Promise<void>; // Force refresh all staff data
  getUserDetailsFromStore: (uid: string) => Promise<{ name: string; photoURL?: string }>;
  
  // Utility
  isStaffCacheValid: (uid?: string) => boolean;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const FULL_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

// Track pending requests to prevent duplicates
const pendingRequests = new Map<string, Promise<StaffUser>>();

// ───────────────────────────────────────────────────────────────────────
// Batch coalescer
// Many components mount in parallel (diary blocks, chat sidebar, billing
// rows, hover popups, etc) and each calls fetchStaffUser. Without
// batching, the network panel shows 30-100 parallel /users/:id/basic-
// details calls in the first second and trips the platform's per-
// endpoint 429 rate limiter. The coalescer collects every UID requested
// during a 50ms window, then fires ONE call to /users/basic-details/batch.
// ───────────────────────────────────────────────────────────────────────
const BATCH_WINDOW_MS = 50;
type BatchEntry = { resolve: (u: any) => void; reject: (e: any) => void };
const batchQueue = new Map<string, BatchEntry[]>();
let batchTimer: ReturnType<typeof setTimeout> | null = null;

const flushBatch = async () => {
  batchTimer = null;
  if (batchQueue.size === 0) return;
  // Snapshot + clear so subsequent calls start a new batch.
  const queue = new Map(batchQueue);
  batchQueue.clear();
  const uids = Array.from(queue.keys());
  try {
    const resp = await brain.get_basic_user_details_batch({ userIds: uids });
    const data: { users?: Record<string, any> } = await resp.json();
    const users = data.users || {};
    for (const uid of uids) {
      const entries = queue.get(uid) || [];
      const userData = users[uid];
      if (userData) {
        entries.forEach((e) => e.resolve(userData));
      } else {
        // Missing in the batch response — resolve as a soft "unknown" so the
        // caller's fallback path still works (don't reject, would log noise).
        entries.forEach((e) => e.resolve(null));
      }
    }
  } catch (err) {
    // Network / 429 / server error — reject every waiter so each call site
    // takes its existing error branch (which falls back to a cached value).
    for (const uid of uids) {
      const entries = queue.get(uid) || [];
      entries.forEach((e) => e.reject(err));
    }
  }
};

const fetchBatchedUserDetails = (uid: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const arr = batchQueue.get(uid) || [];
    arr.push({ resolve, reject });
    batchQueue.set(uid, arr);
    if (!batchTimer) {
      batchTimer = setTimeout(flushBatch, BATCH_WINDOW_MS);
    }
  });
};

export const useStaffStore = create<StaffStore>((set, get) => ({
  // Initial state
  staffUsers: new Map(),
  isLoading: false,
  lastFetchTime: 0,
  
  // Get staff user from cache
  getStaffUser: (uid: string) => {
    const staff = get().staffUsers.get(uid);
    if (!staff) return null;
    
    // Check if cache is still valid
    const now = Date.now();
    if (now - staff.lastUpdated > CACHE_DURATION) {
      // Cache is stale, trigger a fetch but return current data
      get().fetchStaffUser(uid);
    }
    
    return staff;
  },
  
  // Fetch individual staff user
  fetchStaffUser: async (uid: string): Promise<StaffUser> => {
    const currentStaff = get().staffUsers.get(uid);
    const now = Date.now();
    
    // Return cached data if it's fresh
    if (currentStaff && (now - currentStaff.lastUpdated) < CACHE_DURATION) {
      return currentStaff;
    }
    
    // Check for pending request
    const pendingRequest = pendingRequests.get(uid);
    if (pendingRequest) {
      return pendingRequest;
    }
    
    // Create new request
    const requestPromise = (async () => {
      try {
        const userData = await fetchBatchedUserDetails(uid);
        if (!userData) {
          // Batch endpoint returned no record for this UID. Treat as
          // missing user — fall through to fallback below.
          throw new Error('not_found');
        }

        const staffUser: StaffUser = {
          uid,
          name: userData.name || 'Unknown User',
          email: userData.email,
          photoURL: userData.photoURL,
          role: userData.role,
          isActive: userData.isActive,
          isSuperAdmin: userData.isSuperAdmin,
          hideFromClientView: userData.hideFromClientView,
          lastUpdated: now
        };
        
        // Update store
        set(state => {
          const newStaffUsers = new Map(state.staffUsers);
          newStaffUsers.set(uid, staffUser);
          return { staffUsers: newStaffUsers };
        });
        
        return staffUser;
      } catch (error) {
        // Silent for the "this UID has no profile doc" case — the
        // fallback below handles it. Log only true network / server
        // errors so they're still discoverable.
        if (!(error instanceof Error && error.message === 'not_found')) {
          console.error('Error fetching staff user:', error);
        }
        
        // Return cached data if available, otherwise fallback
        if (currentStaff) {
          return currentStaff;
        }
        
        const fallbackUser: StaffUser = {
          uid,
          name: 'Unknown User',
          lastUpdated: now
        };
        
        // Cache the fallback briefly
        set(state => {
          const newStaffUsers = new Map(state.staffUsers);
          newStaffUsers.set(uid, fallbackUser);
          return { staffUsers: newStaffUsers };
        });
        
        return fallbackUser;
      } finally {
        pendingRequests.delete(uid);
      }
    })();
    
    pendingRequests.set(uid, requestPromise);
    return requestPromise;
  },
  
  // Fetch all staff (could be extended to use a bulk API endpoint)
  fetchAllStaff: async () => {
    const { lastFetchTime, isLoading } = get();
    const now = Date.now();
    
    // Skip if recently fetched or already loading
    if (isLoading || (now - lastFetchTime) < FULL_REFRESH_INTERVAL) {
      return;
    }
    
    set({ isLoading: true });
    
    try {
      // This could be replaced with a bulk endpoint if available
      // For now, we'll rely on individual fetches as needed
      set({ lastFetchTime: now });
    } catch (error) {
      console.error('Error fetching all staff:', error);
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Load all staff members using brain.list_staff_members for bulk loading
  loadAllStaffMembers: async (): Promise<StaffUser[]> => {
    const { isLoading } = get();
    
    // Skip if already loading
    if (isLoading) {
      // Return cached data if available
      return Array.from(get().staffUsers.values());
    }
    
    set({ isLoading: true });
    
    try {
      const response = await brain.list_staff_members();
      
      if (!response.ok) {
        throw new Error('Failed to load staff members');
      }
      
      const data = await response.json();
      const staffArray = data.staff_members || [];
      
      const now = Date.now();
      
      // Convert to StaffUser format and cache all staff
      const staffUsers: StaffUser[] = staffArray.map((staff: any) => ({
        uid: staff.id,
        name: `${staff.firstName || ''} ${staff.lastName || ''}`.trim() || 'Unknown User',
        email: staff.email,
        photoURL: staff.photo_url,
        role: staff.role,
        isActive: staff.isActive,
        employmentType: staff.employmentType,  // Add employmentType mapping
        isSuperAdmin: staff.isSuperAdmin,  // Add super admin mapping
        hideFromClientView: staff.hideFromClientView,  // Add hide from client view mapping
        lastUpdated: now
      }));
      
      // Update store with all staff
      set(state => {
        const newStaffUsers = new Map(state.staffUsers);
        staffUsers.forEach(staff => {
          newStaffUsers.set(staff.uid, staff);
        });
        return { 
          staffUsers: newStaffUsers,
          lastFetchTime: now
        };
      });
      
      return staffUsers;
    } catch (error) {
      console.error('Error loading all staff members:', error);
      // Return cached data if available
      return Array.from(get().staffUsers.values());
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Update staff user in cache
  updateStaffUser: (uid: string, updates: Partial<StaffUser>) => {
    set(state => {
      const currentStaff = state.staffUsers.get(uid);
      if (!currentStaff) return state;
      
      const updatedStaff: StaffUser = {
        ...currentStaff,
        ...updates,
        lastUpdated: Date.now()
      };
      
      const newStaffUsers = new Map(state.staffUsers);
      newStaffUsers.set(uid, updatedStaff);
      return { staffUsers: newStaffUsers };
    });
  },
  
  // Clear all staff data
  clearStaff: () => {
    set({ 
      staffUsers: new Map(), 
      lastFetchTime: 0,
      isLoading: false 
    });
    pendingRequests.clear();
  },
  
  // Force refresh all staff data (clears cache and fetches fresh data)
  forceRefreshStaff: async () => {
    // Clear existing data
    set({ 
      staffUsers: new Map(), 
      lastFetchTime: 0,
      isLoading: true 
    });
    pendingRequests.clear();
    
    // Fetch fresh data
    try {
      await get().fetchAllStaff();
      console.log('✅ Staff data force refreshed');
    } catch (error) {
      console.error('❌ Error force refreshing staff:', error);
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Check if cache is valid
  isStaffCacheValid: (uid?: string) => {
    const now = Date.now();
    
    if (uid) {
      const staff = get().staffUsers.get(uid);
      return staff ? (now - staff.lastUpdated) < CACHE_DURATION : false;
    }
    
    // Check overall cache validity
    const { lastFetchTime } = get();
    return (now - lastFetchTime) < FULL_REFRESH_INTERVAL;
  },
  
  // Get user details from store
  getUserDetailsFromStore: async (uid: string) => {
    // First try to get from cache
    let staff = get().staffUsers.get(uid);
    
    // If not in cache, fetch it
    if (!staff) {
      staff = await get().fetchStaffUser(uid);
    }
    
    return {
      name: staff.name,
      photoURL: staff.photoURL
    };
  }
}));
